import { useEffect, useRef } from "react";
import { Pencil } from "lucide-react";
import AutoFitText from "@/components/board/AutoFitText";

const CARD_WIDTH = 220;
const CARD_MAX_WIDTH = 320;
const POLAROID_STRIP_H = 70;
const TITLE_ONLY_CARD_H = 150;

export default function EvidenceCard({
  card,
  category,
  locked,
  dragging,
  onDragStart,
  onEdit,
  registerSize,
}) {
  const rootRef = useRef(null);
  const catColor = category?.color || "#8B5CF6";
  const catName = category?.name || "Uncategorized";
  const isPolaroid = !!card.photo;
  const hasCaption = !!(card.caption && card.caption.trim());

  useEffect(() => {
    if (!rootRef.current) return;
    const el = rootRef.current;
    const ro = new ResizeObserver(() => {
      registerSize(card.id, el.offsetWidth, el.offsetHeight);
    });
    ro.observe(el);
    registerSize(card.id, el.offsetWidth, el.offsetHeight);
    return () => ro.disconnect();
  }, [card.id, registerSize, card.photo, card.text, card.caption]);

  return (
    <div
      ref={rootRef}
      data-testid={`evidence-card-${card.id}`}
      className={`absolute card-hover no-select ${locked ? "cursor-pointer" : dragging ? "cursor-grabbing" : "cursor-grab"
        }`}
      style={{
        left: card.x,
        top: card.y,
        transform: `rotate(${card.rotation || 0}deg)`,
        minWidth: CARD_WIDTH,
        width: "max-content",
        maxWidth: isPolaroid ? CARD_MAX_WIDTH : 500,
        zIndex: dragging ? 40 : 20,
        boxShadow: dragging ? "0 24px 40px rgba(0,0,0,0.7)" : undefined,
      }}
      onMouseDown={onDragStart}
      onDoubleClick={(e) => {
        e.stopPropagation();
        onEdit();
      }}
    >
      {/* Pushpin */}
      <div
        className="pushpin"
        style={{ background: catColor }}
        title={catName}
        data-testid={`pushpin-${card.id}`}
      />

      {isPolaroid ? (
        <div className="polaroid pt-3 px-3 pb-2 flex flex-col gap-2">
          {/* Photo — square */}
          <div className="polaroid-photo w-full aspect-square overflow-hidden">
            <img
              src={card.photo}
              alt=""
              draggable={false}
              className="w-full h-full object-cover"
              style={{ filter: "contrast(1.05) saturate(0.9)" }}
            />
          </div>
          {/* Thick white bottom strip — title fills it */}
          <div
            className="px-2 py-2"
            style={{
              height: POLAROID_STRIP_H,
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <AutoFitText
              min={10}
              max={30}
              maxWidth={CARD_MAX_WIDTH - 24 - 16}
              className="typewriter"
              style={{ color: catColor, fontWeight: 700 }}
            >
              <span data-testid={`card-text-${card.id}`}>
                {card.text || "(untitled)"}
              </span>
            </AutoFitText>
          </div>
        </div>
      ) : hasCaption ? (
        // Text card with caption
        <div className="index-card px-4 pt-5 pb-4 min-h-[110px] flex flex-col relative">
          <div
            className="typewriter text-[24px] leading-snug break-words"
            style={{ color: catColor, fontWeight: 700 }}
            data-testid={`card-text-${card.id}`}
          >
            {card.text || "(untitled node)"}
          </div>
          <div className="handwritten text-[#2a2018]/85 text-[17px] leading-tight mt-2 break-words">
            {card.caption}
          </div>
        </div>
      ) : (
        // Title-only text card — title fills entire card
        <div
          className="index-card px-3 py-4 relative"
          style={{
            height: TITLE_ONLY_CARD_H,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <AutoFitText
            min={12}
            max={44}
            maxWidth={CARD_MAX_WIDTH - 24 }
            className="typewriter"
            style={{
              color: catColor,
              fontWeight: 700,
              padding: "0 4px",
            }}
          >
            <span data-testid={`card-text-${card.id}`}>
              {card.text || "(untitled node)"}
            </span>
          </AutoFitText>
        </div>
      )}

      {/* Edit button */}
      {!locked && (
        <button
          data-testid={`edit-card-btn-${card.id}`}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            onEdit();
          }}
          className="absolute -bottom-2 -right-2 w-7 h-7 rounded-full bg-[#1a140c] text-[#f4d9a8] border border-[#5c4426] shadow-md flex items-center justify-center opacity-70 hover:opacity-100 hover:scale-110 transition-all"
          title="Edit card"
        >
          <Pencil size={12} />
        </button>
      )}
    </div>
  );
}
